/**
 * React hook tracking online/offline status + pending sync count.
 *
 * Returns:
 *   - online: boolean (navigator.onLine + window events)
 *   - pendingCount: number (sum across projects/quotes/pendingMedia non-synced)
 *   - refresh: () => Promise<void> — manual refresh callback
 *
 * Lifecycle:
 *   - On mount: fetch initial pendingCount from IDB
 *   - Listens window.online/offline events
 *   - Periodic refresh every 30s (catches SW sync completion даже без message intercept)
 *   - On window.online → delay 1s → bulkPromotePendingToSynced() → refresh count
 *
 * Used by:
 *   - OnlineIndicator component (UI badge)
 *   - Phase B+ form Containers («Сохранено offline» badge)
 *
 * References: ADR-0019 §Q11.6
 */

import { useCallback, useEffect, useState } from 'react';
import { bulkPromotePendingToSynced, getPendingCount } from './syncQueue';

const REFRESH_INTERVAL_MS = 30 * 1000;
const SW_SYNC_DELAY_MS = 1000;

export interface OnlineStatus {
  online: boolean;
  pendingCount: number;
  refresh: () => Promise<void>;
}

export function useOnlineStatus(): OnlineStatus {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { total } = await getPendingCount();
      setPendingCount(total);
    } catch {
      // IDB unavailable — keep current count
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Reconcile online state after hydration — initial useState может вернуть SSR default
    // `true` если navigator был undefined на server; на клиенте navigator.onLine может быть false.
    if (typeof navigator !== 'undefined') {
      setOnline(navigator.onLine);
    }

    const onOnline = () => {
      setOnline(true);
      // Дать SW время replay queue, потом promote pending → synced
      window.setTimeout(() => {
        void (async () => {
          try {
            await bulkPromotePendingToSynced();
            await refresh();
          } catch {
            // Best effort — IDB unavailable handled внутри
          }
        })();
      }, SW_SYNC_DELAY_MS);
    };
    const onOffline = () => setOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    void refresh();

    const intervalId = window.setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.clearInterval(intervalId);
    };
  }, [refresh]);

  return { online, pendingCount, refresh };
}
